import { MAX_UNWRAP_ITERATIONS } from "../constants/meetings";
import {
  ECalendarConnectionStatus,
  EConnectedCalendarType,
  type ICalendarConnectionGroup,
  type ICalendarConnectionSummary,
  type ICalendarEventItem,
  type ICalendarProviderGroup,
  type IGroupMeetingsByCalendarConnectionProps,
  type IGroupMeetingsByProviderProps,
  type TConnectedCalendar,
} from "../types/calendar";

export interface IBuildConnectedCalendarsProps {
  connections: ICalendarConnectionSummary[];
}

// NOTE: Extracts the inner URL from known calendar redirect wrappers:
// NOTE: - Google redirect: https://www.google.com/url?q=<meeting-url>
// NOTE: - Microsoft Safe Links: https://*.safelinks.protection.outlook.com/?url=<meeting-url>
// NOTE: Loops until no more wrapper layers remain to handle double-wrapped URLs
// NOTE: (e.g. Google redirect wrapped again by Outlook Safe Links in corporate mail flow).
// NOTE: Capped at MAX_UNWRAP_ITERATIONS to guard against pathological self-referential URLs.
export const unwrapCalendarRedirectUrl = (url: string): string => {
  let current = url;

  for (let iteration = 0; iteration < MAX_UNWRAP_ITERATIONS; iteration++) {
    try {
      const parsed = new URL(current);

      if (
        parsed.hostname === "www.google.com" &&
        parsed.pathname === "/url" &&
        parsed.searchParams.has("q")
      ) {
        current = parsed.searchParams.get("q") ?? current;
        continue;
      }

      if (
        parsed.hostname.endsWith(".safelinks.protection.outlook.com") &&
        parsed.searchParams.has("url")
      ) {
        current = parsed.searchParams.get("url") ?? current;
        continue;
      }
    } catch {
      // NOTE: If URL parsing fails, return the current value unchanged
      return current;
    }

    return current;
  }

  return current;
};

export const buildConnectedCalendars = ({
  connections,
}: IBuildConnectedCalendarsProps): TConnectedCalendar[] => {
  return connections.map((connection) => ({
    ...connection,
    type: connection.provider as EConnectedCalendarType,
  }));
};

// NOTE: Only OAuth providers (Google/Microsoft) hold a token that can expire,
// so only they ever surface a "needs reconnect" state. Exchange (Basic auth)
// and iCal (public URL) have nothing to reconnect and are always considered
// connected.
const OAUTH_CALENDAR_PROVIDERS: readonly EConnectedCalendarType[] = [
  EConnectedCalendarType.GOOGLE,
  EConnectedCalendarType.MICROSOFT,
];

export const shouldCalendarReconnect = (
  calendar: TConnectedCalendar,
): boolean =>
  OAUTH_CALENDAR_PROVIDERS.includes(calendar.type) &&
  calendar.status === ECalendarConnectionStatus.NEEDS_RECONNECT;

// NOTE: Number of calendars that are actually connected — i.e. every row that
// is NOT showing "Needs Reconnect". Drives the "N Connected" badge so the count
// matches the per-row status badges instead of counting every connection.
export const countConnectedCalendars = (
  calendars: TConnectedCalendar[],
): number =>
  calendars.filter((calendar) => !shouldCalendarReconnect(calendar)).length;

export const NO_PROVIDER_KEY = "__no_provider__";

export const CALENDAR_PROVIDER_LABEL: Record<string, string> = {
  [EConnectedCalendarType.GOOGLE]: "Google Calendar",
  [EConnectedCalendarType.MICROSOFT]: "Outlook Calendar",
  [EConnectedCalendarType.ICS]: "iCal",
  [NO_PROVIDER_KEY]: "Other",
};

export const getCalendarConnectionDisplayTitle = (
  connection: ICalendarConnectionSummary,
): string => {
  const displayName = connection.displayName?.trim();
  if (displayName) {
    return displayName;
  }

  const email = connection.email?.trim();
  if (email) {
    return email;
  }

  const calendarLink = connection.calendarLink?.trim();
  if (calendarLink) {
    return calendarLink;
  }

  return (
    CALENDAR_PROVIDER_LABEL[connection.provider] ??
    CALENDAR_PROVIDER_LABEL[NO_PROVIDER_KEY]
  );
};

export const getCalendarConnectionGroupHeaderLabel = (
  group: ICalendarConnectionGroup,
): string => {
  if (group.connection) {
    return getCalendarConnectionDisplayTitle(group.connection);
  }

  return (
    CALENDAR_PROVIDER_LABEL[group.provider] ??
    CALENDAR_PROVIDER_LABEL[NO_PROVIDER_KEY]
  );
};

export const groupMeetingsByCalendarConnection = ({
  meetings,
  connections,
}: IGroupMeetingsByCalendarConnectionProps): ICalendarConnectionGroup[] => {
  const connectionsByColor = new Map<string, ICalendarConnectionSummary>();

  for (const connection of connections) {
    if (connection.color) {
      connectionsByColor.set(connection.color, connection);
    }
  }

  const groups = new Map<string, ICalendarConnectionGroup>();

  for (const meeting of meetings) {
    const matchedConnection =
      meeting.color !== null
        ? (connectionsByColor.get(meeting.color) ?? null)
        : null;
    const groupKey =
      matchedConnection?.uuid ?? `${meeting.provider ?? NO_PROVIDER_KEY}`;

    const existingGroup = groups.get(groupKey);

    if (existingGroup) {
      existingGroup.meetings.push(meeting);
      continue;
    }

    groups.set(groupKey, {
      provider:
        matchedConnection?.provider ?? meeting.provider ?? NO_PROVIDER_KEY,
      connection: matchedConnection,
      meetings: [meeting],
    });
  }

  return Array.from(groups.values()).sort((groupA, groupB) =>
    (groupA.meetings[0]?.startTime ?? "").localeCompare(
      groupB.meetings[0]?.startTime ?? "",
    ),
  );
};

export const groupMeetingsByProvider = ({
  meetings,
}: IGroupMeetingsByProviderProps): ICalendarProviderGroup[] => {
  const groups = new Map<string, ICalendarEventItem[]>();

  for (const meeting of meetings) {
    const key = meeting.provider ?? NO_PROVIDER_KEY;
    const group = groups.get(key) ?? [];
    group.push(meeting);
    groups.set(key, group);
  }

  return Array.from(groups.entries())
    .sort(([, aItems], [, bItems]) =>
      (aItems[0]?.startTime ?? "").localeCompare(bItems[0]?.startTime ?? ""),
    )
    .map(([provider, providerMeetings]) => ({
      provider,
      meetings: providerMeetings,
    }));
};
